import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, toast } from '../App';
import { useAuth } from '../contexts/AuthContext';
import { Globe, Plus, Trash2, Lock, Edit, CheckCircle, XCircle, WifiOff, Wifi } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

const WordPressSites = () => {
  const { user } = useAuth();
  const isCapitaineOrAbove = ['amiral', 'capitaine'].includes(user?.grade);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    username: '',
    password: '',
    pitch: '',
    default_prompt: '',
    style_prompt: ''
  });

  useEffect(() => {
    loadSites();
  }, []);

  const loadSites = async () => {
    try {
      const response = await axios.get(`${API}/wordpress-sites`);
      setSites(response.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!formData.name || !formData.url || !formData.username || !formData.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    try {
      const response = await axios.post(`${API}/wordpress-sites`, formData);
      const newSite = response.data;
      
      if (newSite.is_connected === false) {
        toast.warning(`Site "${newSite.name}" ajouté mais NON CONNECTÉ. Vous pouvez l'utiliser pour la création d'articles.`, {
          duration: 8000
        });
      } else {
        toast.success('Site ajouté — connexion WordPress vérifiée');
      }
      
      setShowAdd(false);
      setFormData({ name: '', url: '', username: '', password: '', pitch: '', default_prompt: '', style_prompt: '' });
      loadSites();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erreur lors de l\'ajout';
      toast.error(detail);
    }
  };

  const handleEdit = (site) => {
    setEditingSite(site);
    setFormData({
      name: site.name,
      url: site.url,
      username: site.username,
      password: site.password,
      pitch: site.pitch || '',
      default_prompt: site.default_prompt || '',
      style_prompt: site.style_prompt || ''
    });
    setShowEdit(true);
  };

  const handleUpdate = async () => {
    if (!formData.name || !formData.url || !formData.username || !formData.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    try {
      await axios.put(`${API}/wordpress-sites/${editingSite.id}`, formData);
      toast.success('Site mis à jour — connexion vérifiée');
      setShowEdit(false);
      setEditingSite(null);
      setFormData({ name: '', url: '', username: '', password: '', pitch: '', default_prompt: '', style_prompt: '' });
      loadSites();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erreur lors de la mise à jour';
      toast.error(detail);
    }
  };

  const handleTestConnection = async (siteId) => {
    setTestResults({ ...testResults, [siteId]: 'testing' });
    try {
      const response = await axios.post(`${API}/wordpress-sites/${siteId}/test`);
      setTestResults({ ...testResults, [siteId]: response.data });
    } catch (error) {
      setTestResults({ ...testResults, [siteId]: { success: false, error: 'Erreur de test' } });
    }
  };

  const handleDelete = async (siteId) => {
    if (!window.confirm('Supprimer ce site ?')) return;

    try {
      await axios.delete(`${API}/wordpress-sites/${siteId}`);
      toast.success('Site supprimé');
      loadSites();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-8" data-testid="wordpress-sites-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 md:mb-12 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-orbitron tracking-widest uppercase text-sky-400 text-glow mb-2">
              Sites WordPress
            </h1>
            <p className="text-base md:text-lg text-slate-400 font-rajdhani tracking-wide">
              Configurez vos sites pour la publication automatique
            </p>
          </div>

          {isCapitaineOrAbove && <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild>
              <Button
                data-testid="btn-add-site"
                className="bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 border-2 border-sky-500/50 hover:border-sky-400 uppercase tracking-widest font-bold px-4 py-4 md:px-6 md:py-6 text-sm transition-all duration-300 glow-sky hover:glow-sky-strong whitespace-nowrap"
              >
                <Plus className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                Ajouter
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-950 border-sky-500/50 max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl md:text-2xl font-orbitron text-sky-400">Ajouter un site WordPress</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="text-sm text-slate-400 uppercase tracking-wider">Nom du site</label>
                  <Input
                    data-testid="input-site-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Mon Blog"
                    className="mt-2 bg-slate-950/50 border-slate-800 text-slate-100 placeholder:text-slate-600 focus:border-sky-500/50 focus:ring-1 focus:ring-sky-500/50"
                  />
                </div>

                <div>
                  <label className="text-sm text-slate-400 uppercase tracking-wider">URL du site</label>
                  <Input
                    data-testid="input-site-url"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    placeholder="https://monsite.com"
                    className="mt-2 bg-slate-950/50 border-slate-800 text-slate-100 placeholder:text-slate-600 focus:border-sky-500/50 focus:ring-1 focus:ring-sky-500/50"
                  />
                </div>

                <div>
                  <label className="text-sm text-slate-400 uppercase tracking-wider">Nom d'utilisateur</label>
                  <Input
                    data-testid="input-site-username"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="admin"
                    className="mt-2 bg-slate-950/50 border-slate-800 text-slate-100 placeholder:text-slate-600 focus:border-sky-500/50 focus:ring-1 focus:ring-sky-500/50"
                  />
                </div>

                <div>
                  <label className="text-sm text-slate-400 uppercase tracking-wider">Mot de passe</label>
                  <Input
                    data-testid="input-site-password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    className="mt-2 bg-slate-950/50 border-slate-800 text-slate-100 placeholder:text-slate-600 focus:border-sky-500/50 focus:ring-1 focus:ring-sky-500/50"
                  />
                </div>

                <div>
                  <label className="text-sm text-slate-400 uppercase tracking-wider">Angle éditorial (optionnel)</label>
                  <Textarea
                    data-testid="input-site-pitch"
                    value={formData.pitch || ''}
                    onChange={(e) => setFormData({ ...formData, pitch: e.target.value })}
                    placeholder="Ex: Angle économie grand public, ton pédagogique, focus vulgarisation"
                    className="mt-2 bg-slate-950/50 border-slate-800 text-slate-100 min-h-[80px] text-sm"
                  />
                  <p className="text-xs text-slate-500 mt-1">Claude adaptera chaque article à cet angle spécifique</p>
                </div>

                <div>
                  <label className="text-sm text-slate-400 uppercase tracking-wider">Prompt par défaut (optionnel)</label>
                  <Textarea
                    data-testid="input-site-default-prompt"
                    value={formData.default_prompt || ''}
                    onChange={(e) => setFormData({ ...formData, default_prompt: e.target.value })}
                    placeholder="Ex: Rédige un article structuré avec une introduction percutante, 3 parties avec sous-titres..."
                    className="mt-2 bg-slate-950/50 border-slate-800 text-slate-100 min-h-[80px] text-sm font-mono"
                  />
                  <p className="text-xs text-slate-500 mt-1">Ce prompt sera utilisé par défaut lors de chaque génération pour ce site</p>
                </div>

                {user?.grade === 'amiral' && (
                  <div>
                    <label className="text-sm text-sky-400/80 uppercase tracking-wider">Instructions de style (post-generation)</label>
                    <Textarea
                      data-testid="input-site-style-prompt"
                      value={formData.style_prompt || ''}
                      onChange={(e) => setFormData({ ...formData, style_prompt: e.target.value })}
                      placeholder="Ex: Utilise un ton incisif et des phrases courtes. Privilegie le vocabulaire economique precis..."
                      className="mt-2 bg-slate-950/50 border-sky-500/30 text-slate-100 min-h-[80px] text-sm font-mono"
                    />
                    <p className="text-xs text-sky-500/50 mt-1">Applique apres la generation pour ameliorer le style de chaque article sur ce site</p>
                  </div>
                )}

                <Button
                  onClick={handleAdd}
                  data-testid="btn-submit-site"
                  className="w-full bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 border-2 border-sky-500/50 hover:border-sky-400 uppercase tracking-widest font-bold py-4 md:py-6 text-sm md:text-base"
                >
                  Ajouter
                </Button>
              </div>
            </DialogContent>
          </Dialog>}

          {/* Edit Dialog */}
          <Dialog open={showEdit} onOpenChange={setShowEdit}>
            <DialogContent className="bg-slate-950 border-sky-500/50 max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl md:text-2xl font-orbitron text-sky-400">Modifier le site</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="text-sm text-slate-400 uppercase tracking-wider">Nom du site</label>
                  <Input
                    data-testid="input-edit-site-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-2 bg-slate-950/50 border-slate-800 text-slate-100"
                  />
                </div>

                <div>
                  <label className="text-sm text-slate-400 uppercase tracking-wider">URL du site</label>
                  <Input
                    data-testid="input-edit-site-url"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    placeholder="https://monsite.com"
                    className="mt-2 bg-slate-950/50 border-slate-800 text-slate-100"
                  />
                </div>

                <div>
                  <label className="text-sm text-slate-400 uppercase tracking-wider">Nom d'utilisateur</label>
                  <Input
                    data-testid="input-edit-site-username"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="mt-2 bg-slate-950/50 border-slate-800 text-slate-100"
                  />
                </div>

                <div>
                  <label className="text-sm text-slate-400 uppercase tracking-wider">Mot de passe</label>
                  <Input
                    data-testid="input-edit-site-password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="mt-2 bg-slate-950/50 border-slate-800 text-slate-100"
                  />
                </div>

                <div>
                  <label className="text-sm text-slate-400 uppercase tracking-wider">Angle éditorial (optionnel)</label>
                  <Textarea
                    data-testid="input-edit-site-pitch"
                    value={formData.pitch || ''}
                    onChange={(e) => setFormData({ ...formData, pitch: e.target.value })}
                    placeholder="Ex: Angle économie grand public, ton pédagogique, focus vulgarisation"
                    className="mt-2 bg-slate-950/50 border-slate-800 text-slate-100 min-h-[80px] text-sm"
                  />
                  <p className="text-xs text-slate-500 mt-1">Claude adaptera chaque article à cet angle spécifique</p>
                </div>

                <div>
                  <label className="text-sm text-slate-400 uppercase tracking-wider">Prompt par défaut (optionnel)</label>
                  <Textarea
                    data-testid="input-edit-site-default-prompt"
                    value={formData.default_prompt || ''}
                    onChange={(e) => setFormData({ ...formData, default_prompt: e.target.value })}
                    placeholder="Ex: Rédige un article structuré avec une introduction percutante, 3 parties avec sous-titres..."
                    className="mt-2 bg-slate-950/50 border-slate-800 text-slate-100 min-h-[80px] text-sm font-mono"
                  />
                  <p className="text-xs text-slate-500 mt-1">Ce prompt sera utilisé par défaut lors de chaque génération pour ce site</p>
                </div>

                {user?.grade === 'amiral' && (
                  <div>
                    <label className="text-sm text-sky-400/80 uppercase tracking-wider">Instructions de style (post-generation)</label>
                    <Textarea
                      data-testid="input-edit-site-style-prompt"
                      value={formData.style_prompt || ''}
                      onChange={(e) => setFormData({ ...formData, style_prompt: e.target.value })}
                      placeholder="Ex: Utilise un ton incisif et des phrases courtes. Privilegie le vocabulaire economique precis..."
                      className="mt-2 bg-slate-950/50 border-sky-500/30 text-slate-100 min-h-[80px] text-sm font-mono"
                    />
                    <p className="text-xs text-sky-500/50 mt-1">Applique apres la generation pour ameliorer le style de chaque article sur ce site</p>
                  </div>
                )}

                <Button
                  onClick={handleUpdate}
                  data-testid="btn-update-site"
                  className="w-full bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 border-2 border-sky-500/50 hover:border-sky-400 uppercase tracking-widest font-bold py-4 md:py-6 text-sm md:text-base"
                >
                  Mettre à jour
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <p className="text-slate-400 text-center py-12">Chargement...</p>
        ) : sites.length === 0 ? (
          <Card className="relative bg-slate-950/40 backdrop-blur-xl border-slate-800/60 hud-marker">
            <CardContent className="p-12 text-center">
              <Globe className="w-16 h-16 mx-auto mb-4 text-slate-600" />
              <p className="text-slate-400 text-lg">Aucun site WordPress configuré</p>
              <p className="text-slate-500 text-sm mt-2">Ajoutez un site pour commencer à publier</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sites.map((site) => (
              <Card
                key={site.id}
                data-testid={`site-card-${site.id}`}
                className="relative bg-slate-950/40 backdrop-blur-xl border-slate-800/60 overflow-hidden group hover:border-sky-500/30 transition-all duration-300 hud-marker"
              >
                <CardHeader>
                  <CardTitle className="text-lg md:text-xl font-rajdhani text-sky-300 flex items-start justify-between">
                    <div className="flex items-center flex-1 min-w-0">
                      <Globe className="w-4 h-4 md:w-5 md:h-5 mr-2 text-purple-400 flex-shrink-0" />
                      <span className="truncate">{site.name}</span>
                      {/* Badge de statut de connexion */}
                      {site.is_connected === false ? (
                        <span className="ml-2 flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 text-amber-400 text-[10px] font-bold rounded-full flex-shrink-0">
                          <WifiOff className="w-3 h-3" />
                          NON CONNECTÉ
                        </span>
                      ) : (
                        <span className="ml-2 flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-400 text-[10px] font-bold rounded-full flex-shrink-0">
                          <Wifi className="w-3 h-3" />
                          CONNECTÉ
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2 ml-2 flex-shrink-0">
                      {isCapitaineOrAbove && <button
                        onClick={() => handleEdit(site)}
                        data-testid={`btn-edit-site-${site.id}`}
                        className="text-slate-500 hover:text-sky-400 transition-colors"
                        title="Modifier"
                      >
                        <Edit className="w-4 h-4" />
                      </button>}
                      {isCapitaineOrAbove && <button
                        onClick={() => handleDelete(site.id)}
                        data-testid={`btn-delete-site-${site.id}`}
                        className="text-slate-500 hover:text-red-400 transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs md:text-sm text-slate-400 mb-2 break-all">{site.url}</p>
                  <div className="flex items-center text-xs text-slate-500 mb-3">
                    <Lock className="w-3 h-3 mr-1" />
                    {site.username}
                  </div>
                  
                  <Button
                    onClick={() => handleTestConnection(site.id)}
                    disabled={testResults[site.id] === 'testing'}
                    className="w-full text-xs md:text-sm py-2 bg-slate-800/50 hover:bg-slate-700/50 text-slate-300 border-2 border-slate-600 hover:border-slate-400"
                  >
                    {testResults[site.id] === 'testing' ? 'Test...' : 'Tester la connexion'}
                  </Button>
                  
                  {testResults[site.id] && testResults[site.id] !== 'testing' && (
                    <div className={`mt-3 p-2 rounded text-xs ${
                      testResults[site.id].success 
                        ? 'bg-green-500/10 border-2 border-green-500/30 text-green-400' 
                        : 'bg-red-500/10 border-2 border-red-500/30 text-red-400'
                    }`}>
                      {testResults[site.id].success ? (
                        <div className="flex items-start">
                          <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="font-bold">Connexion réussie !</div>
                            {testResults[site.id].user && (
                              <div className="text-xs mt-1">User: {testResults[site.id].user}</div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start">
                          <XCircle className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="font-bold">Échec</div>
                            <div className="text-xs mt-1">{testResults[site.id].error}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WordPressSites;